"use client";
import ModuleLayout from "@/components/layout/ModuleLayout";
// import { ContactRowSkeleton } from "@/components/pages/contacts/ContactRowSkeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { GetClients } from "@/app/actions/clients/actions";
import { SearchParams, getSearchParams } from "@/types/listing";
import { Flex } from "@radix-ui/themes";
import {
  ArrowUpDown,
  FilePlus,
  MoreHorizontal,
  SearchIcon,
} from "lucide-react";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import { Client, Contact } from "../../../types/models";
import {
  ContactFilterBy,
  // DeleteContact,
  GetContacts,
  GetContactsTotalCount,
  type GetContactsProps,
} from "../../actions/contacts/actions";
import { ClientFilter } from "./ClientFilter";

const ITEMS_TO_SHOW = 5;

type Page = {
  id: number;
  offset: number;
  isActive: boolean;
};

type ContactWithTimesheets = Contact & {
  clients: {
    id: string;
    name: string;
    logo: string | null;
  } | null;
};

export default function List({ searchParams }: { searchParams: SearchParams }) {
  const {
    offset = 0,
    limit = ITEMS_TO_SHOW,
    order = "asc",
    orderBy = "last_name",
  } = searchParams;

  const [clientFilter, setClientFilter] = useState<string | undefined>();
  const [clients, setClients] = useState<Client[]>([]);

  const [deleteAlert, setDeleteAlert] = useState(false);
  const [deletedId, setDeletedId] = useState<string>("");

  const [search, setSearch] = useState("");
  const [searchDebounced] = useDebounce(search, 500);

  const [offsetState, setOffsetState] = useState(offset);
  const [limitState, setLimitState] = useState(limit);

  const [pages, setPages] = useState<Page[]>([]);
  const [activePage, setActivePage] = useState<Page>();
  const [pageCount, setPageCount] = useState(0);

  const [contactList, setContactList] = useState<ContactWithTimesheets[]>([]);
  const [total, setTotal] = useState(0);

  const [isLoading, setIsLoading] = useState(true);

  const getContacts = useMemo(
    () =>
      async ({
        offset,
        limit,
        order,
        orderBy,
        search,
        filterBy,
        filterVal,
      }: GetContactsProps) => {
        const { data, error } = await GetContacts({
          offset,
          limit,
          order,
          orderBy,
          search,
          filterBy,
          filterVal,
        });
        if (error) return { error };
        console.log("CONTRACTS", data);
        return { data };
      },
    [],
  );

  const getTotal = useMemo(
    () =>
      async ({
        search,
        filterBy,
        filterVal,
      }: {
        filterBy?: ContactFilterBy;
        filterVal?: string;
        search?: string;
      }) => {
        const { count, error } = await GetContactsTotalCount({
          search,
          filterBy,
          filterVal,
        });
        console.log("cake", error, count);
        if (error) return { error };

        return {
          count,
        };
      },
    [],
  );

  const getClients = useMemo(
    () => async () => {
      const { data, error } = await GetClients();
      if (error) return { error };
      return { data };
    },
    [],
  );

  useEffect(() => {
    getClients().then(({ data }) => {
      if (!data) return;
      setClients(data);
    });

    if (!offset || !limit) {
      const params = getSearchParams(searchParams);
      params.set("offset", offsetState.toString());
      params.set("limit", limitState.toString());

      window.history.pushState(null, "", `?${params.toString()}`);
    }

    getTotal({
      filterBy: clientFilter ? "client" : undefined,
      filterVal: clientFilter,
      search,
    }).then(({ count, error }) => {
      if (error) return;

      console.log("COUNT", count);
      setTotal(count || 0);
      setPageCount(Math.ceil(Number(count) / Number(limit)));
    });
    /* trunk-ignore(eslint/react-hooks/exhaustive-deps) */
  }, []);

  useEffect(() => {
    setIsLoading(true);
    getContacts({
      offset: offsetState,
      limit: limitState,
      order,
      orderBy,
      search,
      filterBy: clientFilter ? "client" : undefined,
      filterVal: clientFilter,
    }).then(({ data, error }) => {
      if (!data) return;
      if (error) return;
      console.log(data);
      setContactList(data);
      setIsLoading(false);
      getTotal({
        filterBy: clientFilter ? "client" : undefined,
        filterVal: clientFilter,
        search,
      }).then(({ count, error }) => {
        if (error) return;
        if (!count) return;

        setTotal(count);
        setPageCount(Math.ceil(Number(count) / Number(limit)));
        setIsLoading(false);
      });
    });
  }, [
    offsetState,
    clientFilter,
    limitState,
    order,
    orderBy,
    searchDebounced,
    getContacts,
    search,
    getTotal,
    limit,
  ]);

  useEffect(() => {
    setOffsetState(Number(offset || 0) || 0);
    setLimitState(Number(limit) || ITEMS_TO_SHOW);
    const findPage = pages.find((page) => page.offset === Number(offset || 0));
    if (findPage) {
      setActivePage(findPage);
    }
  }, [offset, limit, pages]);

  useEffect(() => {
    const pageArray = Array.from({ length: pageCount }, (_, i) => {
      return {
        id: i + 1,
        offset: limit * i,
        isActive: limit * i === offset,
      } as Page;
    });

    setPages(pageArray);
  }, [limit, offset, pageCount]);

  const getInitials = (name?: string) => {
    if (!name) return "";
    const [first, last] = name.split(" ");
    if (!last) {
      if (name.includes("-")) {
        return `${first[0]}${name.split("-")[1][0]}`.toUpperCase();
      } else {
        return `${first[0]}`.toUpperCase();
      }
    }
    return `${first[0]}${last[0]}`.toUpperCase();
  };

  const offsetActual = Number(offset) + 1;

  const gotoPage = (page: Page) => {
    if (page.id === activePage?.id || isLoading) return;

    setIsLoading(true);
    console.log("goto", page);

    const params = getSearchParams(searchParams);
    params.set("offset", page.offset.toString());
    params.set("limit", limitState.toString());

    window.history.pushState(null, "", `?${params.toString()}`);
  };

  const setOrder = (orderBy: string) => {
    const params = getSearchParams(searchParams);
    params.set("orderBy", orderBy);
    params.set("order", order === "asc" ? "desc" : "asc");
    window.history.pushState(null, "", `?${params.toString()}`);
  };

  const previousPage = () => {
    if (activePage?.id === 1) return activePage;

    return pages[activePage?.id ? activePage.id - 2 : 1];
  };

  const nextPage = () => {
    if (activePage?.id === pages.length) return activePage;

    return pages[activePage?.id ? activePage.id : 1];
  };

  const attemptDeleteOfContact = (id: string) => {
    setDeleteAlert(true);
    setDeletedId(id);
  };

  const deleteContact = (e: React.MouseEvent) => {
    e.preventDefault();

    if (deletedId === "") setDeleteAlert(false);

    // DeleteContact(deletedId).then(() => {
    //   setDeletedId("");
    //   setDeleteAlert(false);
    //   getContacts({
    //     offset: offsetState,
    //     limit: limitState,
    //     order,
    //     orderBy,
    //   }).then(({ data: contacts, error }) => {
    //     if (!contacts) return;
    //     if (error) return;
    //     // setContactList(contacts);
    //   });
    //   getTotal({
    //     filterBy: clientFilter ? "status" : undefined,
    //     filterVal: clientFilter,
    //     search,
    //   }).then(({ data, error }) => {
    //     if (error) return;
    //     if (!data) return;
    //     setTotal(data);
    //     setPageCount(Math.ceil(Number(data) / Number(limit)));
    //   });
    // });
  };

  return (
    <ModuleLayout
      title="Contacts"
      subtitle="Manage your contacts"
      header={
        <div className="relative ml-auto flex-1 md:grow-0">
          <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search..."
            onChange={(e) => setSearch(e.target.value)}
            value={search}
            className="w-full rounded-lg bg-background pl-8 md:w-[200px] lg:w-[336px]"
          />
        </div>
      }
      footer={
        <>
          <Flex width="100%">
            <div className="text-xs text-muted-foreground">
              Showing{" "}
              <strong>
                {offsetActual} -{" "}
                {total < Number(limit) + offsetActual
                  ? total
                  : Number(limit) + Number(offset)}
              </strong>{" "}
              of <strong>{total}</strong> contacts
            </div>
          </Flex>

          <Flex>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    className="cursor-pointer"
                    onClick={() => gotoPage(previousPage())}
                  />
                </PaginationItem>
                {pages.map((page) => (
                  <PaginationItem key={page.id}>
                    <PaginationLink
                      isActive={page.id === activePage?.id}
                      onClick={() => gotoPage(page)}
                      className="cursor-pointer"
                    >
                      {page.id}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => gotoPage(nextPage())}
                    className="cursor-pointer"
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </Flex>
        </>
      }
      meta={
        <AlertDialog
          open={deleteAlert}
          onOpenChange={(open) => setDeleteAlert(open)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete your
                account and remove your data from our servers.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction asChild>
                <Button
                  variant="default"
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={(e) => deleteContact(e)}
                >
                  Delete Contact
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      }
    >
      <Flex className="w-full mb-4" justify="between" gap="3">
        <Flex>
          <ClientFilter
            clients={clients}
            setClientFilter={setClientFilter}
            defaultClient={clientFilter}
          />
        </Flex>
        <Button
          size="sm"
          variant="default"
          className="h-7 gap-1 text-sm"
          asChild
        >
          <Link href="/contacts/create">
            <FilePlus className="h-3.5 w-3.5" />
            <span className="sr-only sm:not-sr-only">Create</span>
          </Link>
        </Button>
      </Flex>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className=" w-[50px] px-0">
              <Button
                variant="ghost"
                onClick={() => setOrder("client")}
                className="w-full justify-start"
              >
                Client
                {orderBy === "client" && (
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                )}
              </Button>
            </TableHead>
            <TableHead className="w-1/6 px-0">
              <Button
                variant="ghost"
                onClick={() => setOrder("first_name")}
                className="w-full justify-start"
              >
                First Name
                {orderBy === "first_name" && (
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                )}
              </Button>
            </TableHead>
            <TableHead className="w-1/6  px-0">
              <Button
                variant="ghost"
                onClick={() => setOrder("last_name")}
                className="w-full justify-start"
              >
                Last Name
                {orderBy === "last_name" && (
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                )}
              </Button>
            </TableHead>
            <TableHead className=" w-1/8 hidden md:table-cell px-0">
              <Button
                variant="ghost"
                onClick={() => setOrder("last_name")}
                className="w-full justify-start"
              >
                Email
                {orderBy === "email" && (
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                )}
              </Button>
            </TableHead>
            <TableHead className="w-1/6 hidden md:table-cell px-0">
              <Button
                variant="ghost"
                onClick={() => setOrder("last_name")}
                className="w-full justify-start"
              >
                Phone
                {orderBy === "phone" && (
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                )}
              </Button>
            </TableHead>
            <TableHead className="w-[50px]">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {/* {isLoading && <ContactRowSkeleton number={ITEMS_TO_SHOW} />} */}
          {!isLoading && contactList.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="text-center">
                No contacts found
              </TableCell>
            </TableRow>
          )}
          {!isLoading &&
            contactList.length !== 0 &&
            contactList.map((contact) => (
              <TableRow key={contact.id}>
                <TableCell className="">
                  <Button
                    variant="ghost"
                    className="w-full justify-start p-0 m-0"
                    asChild
                  >
                    <Link href={`/contacts/${contact.id}`}>
                      <Avatar>
                        <AvatarImage src={contact.clients?.logo || ""} />
                        <AvatarFallback>
                          {getInitials(contact.clients?.name)}
                        </AvatarFallback>
                      </Avatar>
                    </Link>
                  </Button>
                </TableCell>

                <TableCell>{contact.first_name}</TableCell>
                <TableCell>{contact.last_name}</TableCell>
                <TableCell className="hidden md:table-cell">
                  {contact.email}
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  {contact.phone ? contact.phone : "-"}
                </TableCell>

                <TableCell className="flex justify-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button aria-haspopup="true" size="icon" variant="ghost">
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Toggle menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuItem asChild>
                        <Link href={`/contacts/${contact.id}`}>View</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem>Edit</DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          attemptDeleteOfContact(contact.id ? contact.id : "")
                        }
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </ModuleLayout>
  );
}
